// ==========================================================================
// TURBO HISTORY GUARD
// Lets one component own a history entry on the current page — pushed so
// Android Back can close it — without Turbo treating the pop that consumes
// that entry as a navigation.
//
// Turbo answers EVERY popstate that lands on one of its entries with a
// restoration visit: there is no same-page short-circuit, so a pop from a
// foreign entry onto Turbo's entry for the same document marks <html
// aria-busy>, re-fetches the page and replaces the body (#12965 — the GOAT
// Connect phone sheet lost the open conversation and the composer draft).
//
// Turbo's handler is a plain window popstate listener registered when
// @hotwired/turbo starts, which it does at import. Browsers invoke listeners
// on one target in registration order (HeadlessChrome 152, measured; the
// capture-listeners-first rule at the target is not implemented there), so
// the only listener guaranteed to run before Turbo's is one registered before
// Turbo is imported. This module is therefore the FIRST import of
// application.js — pinned by spec/config/turbo_history_guard_import_order_spec.rb
// — and imports nothing of Turbo itself.
//
// One entry is armed at a time; the pop that fires it disarms it.
// ==========================================================================

let armed = null

/**
 * Arm the guard for the entry the caller has just pushed on this document.
 *
 * @param {{ href: string, onPop: (result: { samePage: boolean }) => void }} entry
 *   `href` is location.href at push time; `onPop` runs on the pop whether or
 *   not the guard stopped it, so the owner can let go of its entry.
 * @returns {() => void} release — call when the entry is given up by any
 *   other route (the owner stripped it in place); a stale release is a no-op.
 */
export function armSamePagePop({ href, onPop }) {
  const entry = { href, onPop }
  armed = entry
  return () => {
    if (armed === entry) armed = null
  }
}

function handlePopstate(event) {
  if (!armed) return
  const { href, onPop } = armed
  armed = null
  const samePage = window.location.href === href
  // The entry beneath is this same document: there is nothing to restore, so
  // Turbo must not see the pop. A pop that changed the URL is a real
  // navigation and Turbo handles it as usual.
  if (samePage) event.stopImmediatePropagation()
  onPop({ samePage })
}

window.addEventListener("popstate", handlePopstate)

// The React bundle (Vite) cannot import from the esbuild bundle: it reaches
// the guard through window, the way it reaches showConfirmModal.
window.glTurboHistoryGuard = { armSamePagePop }
